import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import api from '../services/api';
import { toast } from 'sonner';
import {
  AlertTriangle,
  Calendar,
  CheckCircle,
  Clock,
  ExternalLink,
  FileText,
  Kanban,
  Plus,
  Search,
  XCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { cn } from '../lib/utils';
import { getCompanyFilterLabel } from '../lib/serviceGroups';

const todayIso = () => new Date().toISOString().split('T')[0];
const money = (value) => `CHF ${(Number(value) || 0).toFixed(2)}`;

const PHASE_LABELS = ['', 'Erfassung', 'Terminierung', 'Auswertung', 'Entscheid'];

function quoteStatus(quote) {
  const today = todayIso();
  if (quote.status === 'accepted') {
    return { key: 'accepted', label: 'Angenommen', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle };
  }
  if (quote.status === 'rejected') {
    return { key: 'rejected', label: 'Abgelehnt', color: 'bg-red-100 text-red-700', icon: XCircle };
  }
  if (quote.deadline && quote.deadline <= today) {
    return { key: 'due', label: 'Fällig', color: 'bg-amber-100 text-amber-800', icon: AlertTriangle };
  }
  return { key: 'open', label: 'Offen', color: 'bg-slate-100 text-slate-700', icon: Clock };
}

function inPeriod(quote, period) {
  if (period === 'all') return true;
  const raw = quote.created_at || quote.deadline;
  if (!raw) return false;
  const date = new Date(raw);
  const now = new Date();
  if (period === 'this-year') return date.getFullYear() === now.getFullYear();
  if (period === 'last-year') return date.getFullYear() === now.getFullYear() - 1;
  if (period === 'this-month') {
    return date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth();
  }
  return true;
}

function monthlyTotals(quotes) {
  const values = Array.from({ length: 12 }, () => 0);
  quotes.forEach(quote => {
    const raw = quote.created_at || quote.deadline;
    if (!raw) return;
    const date = new Date(raw);
    if (!Number.isNaN(date.getTime())) {
      values[date.getMonth()] += Number(quote.totals?.total || 0);
    }
  });
  return values;
}

export default function QuotesOverviewPage() {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [customerFilter, setCustomerFilter] = useState('all');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [period, setPeriod] = useState('this-year');
  const [search, setSearch] = useState('');

  useEffect(() => {
    api.get('/quotes')
      .then(res => setQuotes(res.data))
      .catch(() => toast.error('Offerten konnten nicht geladen werden'))
      .finally(() => setLoading(false));
  }, []);

  const customers = useMemo(() => (
    Array.from(new Set(quotes.map(q => (q.customer_name || '').trim()).filter(Boolean)))
      .sort((a, b) => a.localeCompare(b, 'de-CH'))
  ), [quotes]);

  const companies = useMemo(() => (
    Array.from(new Set(quotes.map(getCompanyFilterLabel).filter(Boolean)))
      .sort((a, b) => a.localeCompare(b, 'de-CH'))
  ), [quotes]);

  const filteredQuotes = useMemo(() => {
    const q = search.trim().toLowerCase();
    return quotes.filter(quote => {
      const status = quoteStatus(quote).key;
      if (statusFilter !== 'all' && status !== statusFilter) return false;
      if (customerFilter !== 'all' && (quote.customer_name || '').trim() !== customerFilter) return false;
      if (companyFilter !== 'all' && getCompanyFilterLabel(quote) !== companyFilter) return false;
      if (!inPeriod(quote, period)) return false;
      if (!q) return true;
      return [
        quote.quote_number,
        quote.title,
        quote.customer_name,
        quote.property_address,
        quote.object_name,
        quote.renter_name,
        quote.monteur_company,
        quote.monteur_name,
        getCompanyFilterLabel(quote),
        quote.deadline,
      ].some(v => (v || '').toString().toLowerCase().includes(q));
    });
  }, [quotes, statusFilter, customerFilter, companyFilter, period, search]);

  const total = filteredQuotes.reduce((sum, q) => sum + Number(q.totals?.total || 0), 0);
  const openTotal = filteredQuotes
    .filter(q => ['open', 'due'].includes(quoteStatus(q).key))
    .reduce((sum, q) => sum + Number(q.totals?.total || 0), 0);
  const acceptedTotal = filteredQuotes
    .filter(q => quoteStatus(q).key === 'accepted')
    .reduce((sum, q) => sum + Number(q.totals?.total || 0), 0);
  const dueCount = filteredQuotes.filter(q => quoteStatus(q).key === 'due').length;
  const monthly = monthlyTotals(filteredQuotes);
  const maxMonth = Math.max(...monthly, 1);
  const monthLabels = ['Jan.', 'Feb.', 'März', 'Apr.', 'Mai', 'Juni', 'Juli', 'Aug.', 'Sept.', 'Okt.', 'Nov.', 'Dez.'];

  return (
    <Layout>
      <div className="p-6 md:p-8 pb-20" data-testid="quotes-overview-page">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <FileText className="w-6 h-6 text-primary" strokeWidth={1.5} />
            <div>
              <h1 className="font-heading font-black text-3xl">Offerten</h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                {filteredQuotes.length} Offerten · Zusammen {money(total)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button asChild variant="outline" className="rounded-sm gap-2">
              <Link to="/quotes/board">
                <Kanban className="w-4 h-4" strokeWidth={1.5} />
                Phasenansicht
              </Link>
            </Button>
            <Button asChild className="rounded-sm gap-2">
              <Link to="/quotes/new">
                <Plus className="w-4 h-4" strokeWidth={1.5} />
                Neue Offerte
              </Link>
            </Button>
          </div>
        </div>

        <div className="flex flex-col gap-3 mb-5">
          <div className="flex flex-wrap gap-2">
            {[
              ['all', 'Alle'],
              ['open', 'Offen'],
              ['due', 'Fällig'],
              ['accepted', 'Angenommen'],
              ['rejected', 'Abgelehnt'],
            ].map(([key, label]) => (
              <button
                key={key}
                onClick={() => setStatusFilter(key)}
                className={cn(
                  'h-10 px-5 rounded-sm border text-sm font-medium transition-colors',
                  statusFilter === key ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border/70 hover:bg-accent'
                )}
              >
                {label}
              </button>
            ))}
            <Select value={customerFilter} onValueChange={setCustomerFilter}>
              <SelectTrigger className="rounded-sm w-48"><SelectValue placeholder="Alle Verwaltungen" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Verwaltungen</SelectItem>
                {customers.map(customer => <SelectItem key={customer} value={customer}>{customer}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={companyFilter} onValueChange={setCompanyFilter}>
              <SelectTrigger className="rounded-sm w-44"><SelectValue placeholder="Alle Firmen" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Firmen</SelectItem>
                {companies.map(company => <SelectItem key={company} value={company}>{company}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="rounded-sm w-40"><SelectValue placeholder="Zeitraum" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="this-year">Dieses Jahr</SelectItem>
                <SelectItem value="this-month">Dieser Monat</SelectItem>
                <SelectItem value="last-year">Letztes Jahr</SelectItem>
                <SelectItem value="all">Alle Jahre</SelectItem>
              </SelectContent>
            </Select>
            <div className="relative min-w-[260px] flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Suchen" className="pl-9 rounded-sm" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          <div className="bg-card border border-border/60 rounded-sm p-5">
            <p className="text-sm text-muted-foreground">Offerten ({filteredQuotes.length})</p>
            <p className="font-heading text-3xl mt-2 text-primary">{money(total)}</p>
          </div>
          <div className="bg-card border border-border/60 rounded-sm p-5">
            <p className="text-sm text-muted-foreground">Offen</p>
            <p className="font-heading text-3xl mt-2 text-[#5b243a]">{money(openTotal)}</p>
            {dueCount > 0 && <p className="text-xs text-amber-700 mt-2">{dueCount} fällige Offerten</p>}
          </div>
          <div className="bg-card border border-border/60 rounded-sm p-5">
            <p className="text-sm text-muted-foreground">Angenommen</p>
            <p className="font-heading text-3xl mt-2 text-emerald-600">{money(acceptedTotal)}</p>
          </div>
        </div>

        <div className="bg-card border border-border/60 rounded-sm p-5 mb-6">
          <div className="h-64 flex items-end gap-3 border-b border-border/70 px-4">
            {monthly.map((value, idx) => (
              <div key={monthLabels[idx]} className="flex-1 h-full flex flex-col justify-end items-center gap-2">
                <div
                  className="w-full max-w-8 bg-primary rounded-t-sm transition-all"
                  style={{ height: `${Math.max(4, (value / maxMonth) * 86)}%` }}
                  title={money(value)}
                />
                <span className="text-xs text-muted-foreground">{monthLabels[idx]}</span>
              </div>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredQuotes.length === 0 ? (
          <div className="text-center py-16">
            <FileText className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" strokeWidth={1} />
            <p className="text-muted-foreground">Keine Offerten gefunden</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredQuotes.map(q => {
              const status = quoteStatus(q);
              const StatusIcon = status.icon;
              return (
                <div key={q.id} className="flex items-center justify-between gap-4 p-4 bg-card border border-border/60 rounded-sm hover:border-primary/30 transition-colors">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-mono text-muted-foreground">{q.quote_number}</span>
                      <span className={cn('flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-sm font-medium', status.color)}>
                        <StatusIcon className="w-3 h-3" />
                        {status.label}
                      </span>
                    </div>
                    <p className="font-medium text-sm text-foreground truncate">{q.title || 'Offerte ohne Titel'}</p>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
                      {q.customer_name && <span className="text-xs text-muted-foreground">{q.customer_name}</span>}
                      {getCompanyFilterLabel(q) && <span className="text-xs text-muted-foreground">{getCompanyFilterLabel(q)}</span>}
                      {q.deadline && (
                        <span className={cn('flex items-center gap-1 text-xs', status.key === 'due' ? 'text-amber-700 font-medium' : 'text-muted-foreground')}>
                          <Calendar className="w-3 h-3" strokeWidth={1.5} />
                          Deadline {new Date(q.deadline + 'T00:00:00').toLocaleDateString('de-CH')}
                        </span>
                      )}
                      <span className="text-xs text-muted-foreground">Phase {q.phase}: {PHASE_LABELS[q.phase] || 'Offerte'}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <span className="text-sm font-mono font-semibold">{money(q.totals?.total)}</span>
                    <Link to={`/quotes/${q.id}`} className="flex items-center gap-1 text-xs text-primary hover:underline">
                      <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5} />
                      Öffnen
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="fixed bottom-0 left-0 lg:left-56 right-0 border-t border-border/70 bg-muted/95 backdrop-blur px-6 py-3">
          <div className="max-w-full flex flex-wrap items-center justify-center gap-x-10 gap-y-2 text-sm">
            <span><b>{filteredQuotes.length}</b> OFFERTEN</span>
            <span>ZUSAMMEN: <b>{money(total)}</b></span>
            <span>OFFEN: <b>{money(openTotal)}</b></span>
          </div>
        </div>
      </div>
    </Layout>
  );
}
